import { isConsumerAppPath, isConsumerTabPath } from "./consumer-navigation.ts"
import { consumerDestinations, resolveUserRoute } from "./routes.ts"

const key = "__cashhubBack"
type Marker = { path: string; from: string | null }
const marker = (state: unknown): Marker | null => {
  if (state === null || typeof state !== "object" || !(key in state)) return null
  const value = (state as Record<string, unknown>)[key]
  if (value === null || typeof value !== "object" || !("path" in value) || !("from" in value)) return null
  return typeof value.path === "string" && (value.from === null || typeof value.from === "string") ? { path: value.path, from: value.from } : null
}

export const consumerBackFallback = (pathname: string): string => consumerDestinations.find(({ id }) => id === resolveUserRoute(pathname)?.destination)?.href ?? "/home"

// Transaction steps must not be restored as the destination of a generic Back.
const canReturnTo = (pathname: string): boolean => {
  const route = resolveUserRoute(pathname)
  return isConsumerAppPath(pathname) && route !== null && route.kind !== "redemption" && route.kind !== "transaction" && route.kind !== "system"
}

export const hasConsumerBack = (): boolean => {
  if (typeof window === "undefined") return false
  const current = marker(window.history.state)
  return current !== null && current.path === window.location.pathname && current.from !== null && current.from !== current.path && canReturnTo(current.from)
}

export const allowsConsumerBackSwipe = (pathname: string): boolean => !isConsumerTabPath(pathname) && canReturnTo(pathname) && hasConsumerBack()

/** Tag browser entries without retaining Next's routing state or changing its navigation. */
export const installConsumerHistory = (): (() => void) => {
  const history = window.history
  const push = history.pushState
  const replace = history.replaceState
  const notify = () => window.dispatchEvent(new Event("cashhub:history-changed"))
  const tag = (data: unknown, path: string, from: string | null) => ({ ...(data !== null && typeof data === "object" ? data : {}), [key]: { path, from } })
  const initial = marker(history.state)
  replace.call(history, tag(history.state, window.location.pathname, initial?.path === window.location.pathname ? initial.from : null), "")
  const wrap =
    (original: History["pushState"], pushing: boolean): History["pushState"] =>
    (data, unused, url) => {
      const before = window.location.pathname
      const previous = marker(history.state)
      const target = url === undefined || url === null ? new URL(window.location.href) : new URL(url, window.location.href)
      const from = pushing && target.pathname !== before ? (canReturnTo(before) ? before : null) : previous?.path === before ? previous.from : null
      original.call(history, tag(data, target.pathname, isConsumerAppPath(target.pathname) ? from : null), unused, url)
      notify()
    }
  const wrappedPush = wrap(push, true)
  const wrappedReplace = wrap(replace, false)
  history.pushState = wrappedPush
  history.replaceState = wrappedReplace
  const clear = () => {
    replace.call(history, tag(history.state, window.location.pathname, null), "")
    notify()
  }
  window.addEventListener("popstate", notify)
  window.addEventListener("cashhub:session-clearing", clear)
  return () => {
    if (history.pushState === wrappedPush) history.pushState = push
    if (history.replaceState === wrappedReplace) history.replaceState = replace
    window.removeEventListener("popstate", notify)
    window.removeEventListener("cashhub:session-clearing", clear)
  }
}
